import { useEffect, useRef, useState, type ReactNode } from 'react';

import { ActivityGlyph } from '~/design-system/components/v1_5/Chat';
import type { TurnBlock } from '../turnModel';
import styles from './WorkSummary.module.css';
import { formatWorkDuration, summarizeWorkBlocks } from './workSummaryData';

/**
 * Whether the header carries its tally — `8 artifacts · 3 actions · 12 steps`.
 *
 * OFF (Beka, 2026-09-20: "no need for that, hide it"). HIDDEN, not deleted, and deliberately so:
 * whether the count earns its place beside the duration is as much Guri's call as his, so this is
 * one boolean to flip rather than a span to reconstruct from the summary object.
 *
 * Nothing else is switched off with it. `summarizeWorkBlocks` still runs on every turn because
 * `hasFailure` decides whether the section opens itself, and that is the half of this header that
 * carries a warning rather than a statistic. So the tally goes quiet and the failure still shouts.
 *
 * Typed `boolean` rather than left to infer `false`, so the branch below stays a real condition to
 * the compiler and the markup inside it keeps being type-checked while it is off.
 */
const SHOW_TALLY: boolean = false;

interface WorkSummaryProps {
  blocks: TurnBlock[];
  children: ReactNode;
  elapsedSec?: number;
  artifactCount?: number;
}

export function WorkSummary({ blocks, children, elapsedSec, artifactCount }: WorkSummaryProps) {
  // Only mounted after the chat AND the host work settle. Details remain available on demand.
  const [collapsed, setCollapsed] = useState(true);

  const summary = summarizeWorkBlocks(blocks, artifactCount);

  /* A FAILURE OPENS ITSELF. Collapsed-by-default is right for work that worked: the agent has already
     said what it did in prose. It is wrong for work that did NOT, because the prose above says the
     opposite — the agent narrates its dispatch, the row underneath holds the refusal, and with the row
     shut the user is told a thing happened that did not. That is the whole of "it says it's dispatched
     but does not indicate anything. It does not do anything." (Guri, 2026-09-05.)
     Once the user has touched the header their choice stands, including a deliberate collapse. */
  const touched = useRef(false);
  useEffect(() => {
    if (summary.hasFailure && !touched.current) setCollapsed(false);
  }, [summary.hasFailure]);
  const title = `${summary.hasFailure ? 'Work stopped' : 'Worked'}${elapsedSec !== undefined ? ` for ${formatWorkDuration(elapsedSec)}` : ''}`;

  return (
    <section className={styles.root} aria-label="Agent work">
      <button
        type="button"
        className={styles.header}
        aria-expanded={!collapsed}
        onClick={() => {
          touched.current = true;
          setCollapsed((current) => !current);
        }}
      >
        <span className={styles.title}>{title}</span>
        {/* The chevron takes its place on the right from `margin-left: auto`, not from this span
            growing, so the header keeps its geometry with the tally off. */}
        {SHOW_TALLY ? (
          <span className={styles.summary}>
            {summary.headline}
            {summary.stepCount > 0 ? ` · ${summary.stepCount} ${summary.stepCount === 1 ? 'step' : 'steps'}` : ''}
          </span>
        ) : null}
        {/*
          * The registry's mark, not the characters `›` and `⌄`.
          *
          * Those two are typographic glyphs from whatever face the row is set in — a single
          * guillemet and a caret — so they sat at a different weight and a different optical size
          * from every other chevron in the chat, which are all `ActivityGlyph`. Beka, 2026-09-20:
          * "put our icon there in a better way." Same door every disclosure in the thread uses now,
          * including the `Thought for …` row directly beneath this one.
          *
          * And it points the way the rest of v1.5 points: DOWN when there is something folded away,
          * UP when it is open — `ActivityRow`'s rule, "an expanded row folds UP, a folded row opens
          * DOWN". It used to point RIGHT while collapsed, which is a disclosure from a different
          * system (a tree, where right means "expands sideways") and read as a link to somewhere
          * else rather than as a fold.
          *
          * 15 up, 14 down, and `tone="inherit"` so the mark takes `.chevron`'s colour: all three are
          * what `ActivityRow` passes, so the two disclosures in this stack cannot drift apart.
          */}
        <span className={styles.chevron} aria-hidden="true">
          <ActivityGlyph
            chrome={collapsed ? 'chevron-down' : 'chevron-up'}
            size={collapsed ? 14 : 15}
            tone="inherit"
          />
        </span>
      </button>
      {!collapsed && <div className={styles.details}>{children}</div>}
    </section>
  );
}
